<script setup lang="ts">
import { useHead } from '@unhead/vue';
import Divider from '@/layout/Divider.vue';
import Layout from '@/layout/Layout.vue';
import locations from '@/scripts/locations';

useHead({
  title: "Albano's Villa | Parties",
  meta: [
    {
      name: 'description',
      content:
        "Host a Make-Your-Own-Pizza party at Albano's Villa on Johnson Road in LaPorte — kids roll, top and bake their own pizza masterpiece. Call to book.",
    },
  ],
});

const johnson = locations[2];

/**
 * Strip non-digits from a phone string for a `tel:` href (e.g. "(219) 879-4000" → "+12198794000").
 */
function telHref(phone: string): string {
  return `tel:+1${phone.replace(/\D/g, '')}`;
}
</script>

<template>
  <Layout sidebar="images-parties">
    <h1>Albano's Make-Your-Own-Pizza Parties</h1>
    <p>
      {{ johnson.address }} | <a :href="telHref(johnson.phone)">{{ johnson.phone }}</a>
    </p>
    <h2>Create a masterpiece … Then eat it!</h2>
    <p>
      If one food is a universal favorite among kids, it is pizza. That gooey cheesy goodness is
      something youngsters look forward to, beg for and cheer about. At Albano's, our pizza is art.
      But we don't limit the creation of these culinary masterpieces to the cooks in the kitchen. We
      invite kids to grab a rolling pin and produce the pizza of their dreams.
    </p>
    <p>
      Albano's Make-Your-Own-Pizza Parties are a great way for kids to have fun with friends while
      also taking part in a unique activity. They learn new skills and get to see their favorite
      pizza place from the other side of the counter. It's a fresh twist on an old favorite. Our
      student chefs get the chance to work side by side with Albano's masters, using fresh toppings
      and ingredients made from scratch. Albano's Little Pizza Artists roll their own dough, spread
      the sauce, sprinkle the cheese and choose their own toppings. And, of course, when their
      edible art comes out of the oven, they get to put their work to the true test - the taste
      test!
    </p>
    <p>
      Whether celebrating a birthday or planning a field trip, an Albano's Make-Your-Own-Pizza Party
      is perfect for kids of all ages. To reserve your party, call
      <a :href="telHref(johnson.phone)">{{ johnson.phone }}</a
      >.
    </p>
    <p>Take a look at the below testimonials from kids and parents who have participated.</p>
    <h2>Testimonials</h2>
    <blockquote>
      <p>
        "It's a great idea. It's great for the kids. They have fun and it keeps them busy during the
        party and they get to do something that's all their own. I've told everyone I know about
        this."
      </p>
      <footer><cite>— Jackie, Satisfied Parent</cite></footer>
    </blockquote>
    <blockquote>
      <p>
        "My favorite thing about the party is that you get to make the pizza, and then you get to
        eat it!"
      </p>
      <footer><cite>— Alex, 7, Albano's Little Pizza Artist</cite></footer>
    </blockquote>
    <blockquote>
      <p>"My favorite thing was making my own pizza and putting the stuff on my pizza."</p>
      <footer><cite>— Javier, 4, Albano's Little Pizza Artist</cite></footer>
    </blockquote>
    <blockquote>
      <p>
        "My pizza had lots of pepperoni. My favorite thing about the party was that we got to make
        it."
      </p>
      <footer><cite>— Petra, 5, Albano's Little Pizza Artist</cite></footer>
    </blockquote>
    <blockquote>
      <p>
        "My very own Albano's Pizza special recipe was pineapple, ham, pepperoni, black olives and
        lots of cheese! Making my own pizza was fun because I can make it as big as I want!"
      </p>
      <footer><cite>— Marcos, 8, Albano's Little Pizza Artist</cite></footer>
    </blockquote>
    <blockquote>
      <p>"My favorite thing about the party was making the pizza and picking my own toppings."</p>
      <footer><cite>— Anastasia, 6, Albano's Little Pizza Artist</cite></footer>
    </blockquote>
    <Divider />
  </Layout>
</template>
